import type { DOMProps } from '@react-types/shared';
import type { ForwardedRef, ImgHTMLAttributes, JSX, ReactNode } from 'react';
import { createContext, forwardRef, useContext } from 'react';
import type { ContextValue, SlotProps } from 'react-aria-components';
import {
  Header as AriaHeader,
  Heading as AriaHeading,
  Text as AriaText,
  TextContext as AriaTextContext,
} from 'react-aria-components';

import type { UnsafeStyleProps } from '@infinex/ui-styles';
import { cn, useContextProps } from '@infinex/ui-styles';

/** Valid elements for components that allow `elementType` to be set. */
export type SlotElement =
  | 'aside'
  | 'div'
  | 'footer'
  | 'header'
  | 'section'
  | 'nav';

export type SlotComponentProps = {
  /**
   * Whether the element should be hidden, within a slotted component.
   */
  isHidden?: boolean;
  /**
   * Wrap the element with some custom behaviour, within a slotted component.
   */
  render?: (element: JSX.Element) => ReactNode;
} & DOMProps &
  UnsafeStyleProps &
  SlotProps;

// Heading
// -----------------------------------------------------------------------------

export type HeadingProps = {
  /** The content of the element. */
  children?: ReactNode;
  /**
   * The heading level.
   * @default 3
   */
  level?: number;
  /**
   * Optionally applies default styles to the heading.
   */
  variant?: 'primary' | 'secondary' | 'tertiary' | 'quaternary';
} & SlotComponentProps;

export const HeadingContext = createContext<
  ContextValue<HeadingProps, HTMLHeadingElement>
>({});

/**
 * Represents a heading within a component-lib container. Provides no styling by
 * itself, but receives cosmetic and layout styles from the parent container.
 */
export const Heading = forwardRef(function Heading(
  props: HeadingProps,
  ref: ForwardedRef<HTMLHeadingElement>
) {
  [props, ref] = useContextProps(props, ref, HeadingContext);

  const { className, isHidden, render, slot, variant, ...otherProps } = props;

  if (isHidden) {
    return null;
  }

  const heading = (
    <AriaHeading
      ref={ref}
      className={cn(
        {
          'text-emphasis leading-tight': !!variant,
          'text-3xl font-semibold': variant === 'primary',
          'text-xl font-semibold': variant === 'secondary',
          'text-lg font-semibold': variant === 'tertiary',
          'text-base font-semibold': variant === 'quaternary',
        },
        className
      )}
      slot={slot || undefined}
      {...otherProps}
    />
  );

  if (render) {
    return render(heading);
  }

  return heading;
});

// Header
// -----------------------------------------------------------------------------

export type HeaderProps = {
  /** The content of the element. */
  children?: ReactNode;
} & SlotComponentProps;

export const HeaderContext = createContext<
  ContextValue<HeaderProps, HTMLElement>
>({});

/**
 * Represents a header within a component-lib container. Provides no styling by
 * itself, but receives cosmetic and layout styles from the parent container.
 */
export const Header = forwardRef(function Header(
  props: HeaderProps,
  ref: ForwardedRef<HTMLElement>
) {
  [props, ref] = useContextProps(props, ref, HeaderContext);

  const { isHidden, render, slot, ...otherProps } = props;

  if (isHidden) {
    return null;
  }

  const header = (
    <AriaHeader ref={ref} slot={slot || 'header'} {...otherProps} />
  );

  if (render) {
    return render(header);
  }

  return header;
});

// Content
// -----------------------------------------------------------------------------

export type ContentProps = {
  /** The content of the element. */
  children?: ReactNode;
  /**
   * The underlying HTML element to render.
   * @default 'div'
   */
  elementType?: SlotElement;
  /**
   * Optionally applies default styles to the content.
   */
  variant?: 'prose' | 'prose-sm' | 'prose-xs';
} & SlotComponentProps;

export const ContentContext = createContext<
  ContextValue<ContentProps, HTMLDivElement>
>({});

/**
 * Represents the primary content area within a component-lib container.
 * Provides no styling by itself, but receives cosmetic and layout styles from
 * the parent container.
 */
export const Content = forwardRef(function Content(
  props: ContentProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  [props, ref] = useContextProps(props, ref, ContentContext);

  const {
    className,
    elementType: Element = 'div',
    isHidden,
    render,
    slot,
    variant,
    ...otherProps
  } = props;

  if (isHidden) {
    return null;
  }

  const content = (
    <Element
      ref={ref}
      className={cn(
        {
          'space-y-[1em]': !!variant,
          'text-base': variant === 'prose',
          'text-sm': variant === 'prose-sm',
          'text-xs': variant === 'prose-xs',
        },
        className
      )}
      slot={slot || 'content'}
      {...otherProps}
    />
  );

  if (render) {
    return render(content);
  }

  return content;
});

// Text
// -----------------------------------------------------------------------------

export type TextProps = {
  /**
   * Indicates whether the element is exposed to an accessibility API.
   */
  'aria-hidden'?: boolean;
  /** The content of the element. */
  children?: ReactNode;
  /**
   * The underlying HTML element to render.
   * @default 'span'
   */
  elementType?: SlotElement;
} & SlotComponentProps;

export const TextContext = createContext<ContextValue<TextProps, HTMLElement>>(
  {}
);

/**
 * Represents text with no specific semantic meaning within a component-lib
 * container. Provides no styling by itself, but receives cosmetic and layout
 * styles from the parent container.
 */
export const Text = forwardRef(function Text(
  props: TextProps,
  ref: ForwardedRef<HTMLElement>
) {
  [props, ref] = useContextProps(props, ref, TextContext);
  const racContext = useContext(AriaTextContext);

  const { isHidden, render, slot, ...otherProps } = props;

  if (isHidden) {
    return null;
  }

  let text = <AriaText ref={ref} slot={slot || undefined} {...otherProps} />;

  if (render) {
    text = render(text) as JSX.Element;
  }

  // Avoid errors being thrown when a slot is provided that's not defined by
  // some RAC provider for TextContext.
  if (
    slot &&
    racContext &&
    'slots' in racContext &&
    !racContext.slots?.[slot]
  ) {
    return (
      <AriaTextContext.Provider value={null}>{text}</AriaTextContext.Provider>
    );
  }

  return text;
});

// Footer
// -----------------------------------------------------------------------------

export type FooterProps = {
  /** The content of the element. */
  children?: ReactNode;
  /**
   * The underlying HTML element to render.
   * @default 'footer'
   */
  elementType?: SlotElement;
} & SlotComponentProps;

export const FooterContext = createContext<
  ContextValue<FooterProps, HTMLDivElement>
>({});

/**
 * Represents a footer within a component-lib container. Provides no styling by
 * itself, but receives cosmetic and layout styles from the parent container.
 */
export const Footer = forwardRef(function Footer(
  props: FooterProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  [props, ref] = useContextProps(props, ref, FooterContext);

  const {
    elementType: Element = 'footer',
    isHidden,
    render,
    slot,
    ...otherProps
  } = props;

  if (isHidden) {
    return null;
  }

  const footer = <Element ref={ref} slot={slot || 'footer'} {...otherProps} />;

  if (render) {
    return render(footer);
  }

  return footer;
});

// Image
// -----------------------------------------------------------------------------

// prettier-ignore
type SupportedImgAttributes = 'crossOrigin' | 'decoding' | 'fetchPriority' | 'height' | 'loading' | 'referrerPolicy' | 'sizes' | 'srcSet' | 'width';
export type ImageProps = {
  /** The URL of the image. */
  src?: string;
  /** Accessible alt text for the image. */
  alt?: string;
} & SlotComponentProps &
  Pick<ImgHTMLAttributes<HTMLImageElement>, SupportedImgAttributes>;

export const ImageContext = createContext<
  ContextValue<ImageProps, HTMLImageElement>
>({});

/**
 * Represents an image within a component-lib container. Provides no styling by
 * itself, but receives cosmetic and layout styles from the parent container.
 */
export const Image = forwardRef(function Image(
  props: ImageProps,
  ref: ForwardedRef<HTMLImageElement>
) {
  [props, ref] = useContextProps(props, ref, ImageContext);

  const { isHidden, render, slot, ...otherProps } = props;

  if (isHidden) {
    return null;
  }

  if (props.alt == null) {
    console.warn(
      'The `alt` prop was not provided to an image. ' +
        'Add `alt` text for screen readers, or set `alt=""` prop to indicate that the image ' +
        'is decorative or redundant with displayed text and should not be announced by screen readers.'
    );
  }

  // eslint-disable-next-line jsx-a11y/alt-text
  const image = <img ref={ref} slot={slot || 'image'} {...otherProps} />;

  if (render) {
    return render(image);
  }

  return image;
});
